import React from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '../../utils/cn';

const selectVariants = cva(
  "inline-flex items-center justify-between w-full rounded-lg border bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer transition-colors",
  {
    variants: {
      variant: {
        default: "border-gray-300 bg-white text-gray-900 focus:border-primary-500 focus:ring-primary-500/20 dark:border-dark-300 dark:bg-dark-200 dark:text-dark-900 dark:focus:border-accent-500 dark:focus:ring-accent-500/20",
        dark: "border-dark-300 bg-dark-200 text-dark-900 focus:border-accent-500 focus:ring-accent-500/20",
        ghost: "border-transparent bg-transparent hover:bg-gray-50 dark:hover:bg-dark-100",
      },
      size: {
        sm: "h-8 px-2 text-xs",
        default: "h-10",
        lg: "h-12 px-4",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export const Select = React.forwardRef(({
  className,
  variant,
  size,
  label,
  error,
  helperText,
  options = [],
  placeholder = "Select an option...",
  children,
  ...props
}, ref) => {
  const id = React.useId();

  return (
    <div className="w-full">
      {label && (
        <label 
          htmlFor={id}
          className="block text-sm font-medium text-gray-700 dark:text-dark-700 mb-2"
        >
          {label}
        </label>
      )}
      
      <div className="relative">
        <select
          id={id}
          ref={ref}
          className={cn(selectVariants({ variant, size }), error && "border-red-500 focus:border-red-500 focus:ring-red-500/20", className)}
          {...props}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
          {children}
        </select>
        
        <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
          <i className="fa-solid fa-chevron-down text-gray-400 dark:text-dark-400 text-xs" />
        </div>
      </div>
      
      {error && (
        <p className="mt-1 text-sm text-red-600 dark:text-red-400">
          {error}
        </p>
      )}
      
      {helperText && !error && (
        <p className="mt-1 text-sm text-gray-500 dark:text-dark-500">
          {helperText}
        </p>
      )}
    </div>
  );
});

Select.displayName = "Select";

// Compact Select for inline use (like time period selectors)
export const CompactSelect = React.forwardRef(({
  className,
  options = [],
  ...props
}, ref) => {
  return (
    <div className="relative">
      <select
        ref={ref}
        className={cn(
          "appearance-none bg-white dark:bg-dark-200 border border-gray-300 dark:border-dark-300 rounded-lg px-3 py-2 pr-8 text-sm text-gray-700 dark:text-dark-700 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-accent-500 focus:border-transparent transition-colors cursor-pointer",
          className
        )}
        {...props}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      
      <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
        <i className="fa-solid fa-chevron-down text-gray-400 dark:text-dark-400 text-xs" />
      </div>
    </div>
  );
});

CompactSelect.displayName = "CompactSelect"; 